
import { useState, useEffect, useRef } from "react";
import { getPayloadById } from "@/utils/payloads";
import { ConfigField } from "@/types/profile";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { X, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface PayloadConfigFormProps {
  payloadId: string;
  initialValues?: Record<string, any>;
  onValuesChange: (values: Record<string, any>) => void;
  onValidationChange?: (isValid: boolean) => void;
  onRemove: () => void;
}

export const PayloadConfigForm = ({
  payloadId,
  initialValues = {},
  onValuesChange,
  onValidationChange,
  onRemove
}: PayloadConfigFormProps) => {
  const payload = getPayloadById(payloadId);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const isMountedRef = useRef(true);
  
  // Initialize state with default values
  const [values, setValues] = useState<Record<string, any>>(() => {
    const defaultValues: Record<string, any> = {};
    
    payload?.configFields.forEach(field => {
      if (initialValues.hasOwnProperty(field.id)) {
        defaultValues[field.id] = initialValues[field.id];
      } else if (field.default !== undefined) {
        defaultValues[field.id] = field.default;
      } else {
        // Set sensible defaults based on field type
        switch (field.type) {
          case "text":
          case "textarea":
            defaultValues[field.id] = "";
            break;
          case "number":
            defaultValues[field.id] = 0;
            break;
          case "boolean":
            defaultValues[field.id] = false;
            break;
          case "select":
            defaultValues[field.id] = field.options?.[0] || "";
            break;
          case "file":
            defaultValues[field.id] = null;
            break;
        }
      }
    });
    
    return defaultValues;
  });

  // Validate all fields
  const validateFields = () => {
    const errors: Record<string, string> = {};
    
    payload?.configFields.forEach(field => {
      if (field.required) {
        const value = values[field.id];
        
        // Check if required field is empty or invalid
        if (value === undefined || value === null || value === "" || 
            (field.type === "file" && !value) ||
            (Array.isArray(value) && value.length === 0)) {
          errors[field.id] = `${field.name} is required`;
        }
      }
    });
    
    // Only update state if component is still mounted
    if (isMountedRef.current) {
      setValidationErrors(errors);
    }

    const isValid = Object.keys(errors).length === 0;
    
    // Notify parent about validation state only if mounted
    if (onValidationChange && isMountedRef.current) {
      onValidationChange(isValid);
    }
    
    return isValid;
  };

  // Validate whenever values change
  useEffect(() => {
    validateFields();
  }, [values]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleValueChange = (fieldId: string, value: any) => {
    const newValues = { ...values, [fieldId]: value };
    setValues(newValues);
    onValuesChange(newValues);
  };

  if (!payload) {
    return <div>Payload not found</div>;
  }

  const renderField = (field: ConfigField) => {
    const hasError = validationErrors[field.id];
    const fieldClassName = hasError ? "border-destructive" : "";

    switch (field.type) {
      case "text":
        return (
          <Input 
            value={values[field.id] || ""}
            onChange={(e) => handleValueChange(field.id, e.target.value)}
            placeholder={field.name}
            className={fieldClassName}
          />
        );
      
      case "number":
        return (
          <Input 
            type="number"
            value={values[field.id]}
            onChange={(e) => handleValueChange(field.id, Number(e.target.value))}
            placeholder={field.name}
            className={fieldClassName}
          />
        );
      
      case "boolean":
        return (
          <Checkbox 
            checked={values[field.id]}
            onCheckedChange={(checked) => handleValueChange(field.id, checked)}
          />
        );
      
      case "textarea":
        return (
          <Textarea 
            value={values[field.id] || ""}
            onChange={(e) => handleValueChange(field.id, e.target.value)}
            placeholder={field.name}
            className={fieldClassName}
          />
        );
      
      case "select":
        return (
          <Select 
            value={values[field.id]}
            onValueChange={(value) => handleValueChange(field.id, value)}
          >
            <SelectTrigger className={fieldClassName}>
              <SelectValue placeholder={field.name} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map(option => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case "file":
        return (
          <Input 
            type="file"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              handleValueChange(field.id, file);
            }}
            className={fieldClassName}
          />
        );
      
      default:
        return null;
    }
  };

  const hasErrors = Object.keys(validationErrors).length > 0;

  return (
    <div className="bg-background border rounded-md p-4 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium text-base">{payload.name}</h3>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onRemove}
          className="h-7 w-7 p-0"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Remove</span>
        </Button>
      </div>

      {hasErrors && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please complete all required fields marked with *
          </AlertDescription>
        </Alert>
      )}
      
      <div className="space-y-4">
        {payload.configFields.map(field => (
          <div key={field.id} className="grid gap-2">
            <div className="flex items-center gap-2">
              <Label htmlFor={field.id} className="flex-1">
                {field.name}
                {field.required && <span className="text-destructive ml-1">*</span>}
              </Label>
              {renderField(field)}
            </div>
            {validationErrors[field.id] && (
              <p className="text-xs text-destructive">{validationErrors[field.id]}</p>
            )}
            {field.description && !validationErrors[field.id] && (
              <p className="text-xs text-muted-foreground">{field.description}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
